import { inject } from '@angular/core';
import { ResolveFn, Routes } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ProjectService } from './services/project.service';
import { ProjectRoleService } from './services/project-role.service';
import { RequirementsService } from './services/requirements.service';
import { AppBreadcrumb } from './layout/breadcrumb/app-breadcrumb.component';
import { authGuard } from './core/auth/auth.guard';
import { logoutGuard } from './core/auth/logout.guard';
import { platformAdminGuard } from './core/auth/platform-admin.guard';
import { tenantAdminGuard } from './core/auth/tenant-admin.guard';
import { LoginComponent } from './login/login.component';
import { MainLayoutComponent } from './layout/main-layout/main-layout.component';
import { HomeComponent } from './home/home.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { TestResultsComponent } from './dashboard/test-results/test-results.component';
import { canDeactivateTestResults } from './dashboard/test-results/can-deactivate-test-results.guard';
import { TestResultDetailComponent } from './dashboard/test-result-detail/test-result-detail.component';
import { TestResultHistoryComponent } from './dashboard/test-result-history/test-result-history.component';
import { canDeactivateRequirementForm } from './dashboard/requirements/requirement-form/can-deactivate-requirement-form.guard';
import { RequirementHistoryComponent } from './dashboard/requirements/requirement-history/requirement-history.component';
import { TestCaseComponent } from './dashboard/test-case/test-case.component';
import { TestCaseFormComponent } from './dashboard/test-case/test-case-form/test-case-form.component';
import { canDeactivateTestCaseForm } from './dashboard/test-case/test-case-form/can-deactivate-test-case-form.guard';
import { testCaseKeyBreadcrumbResolver } from './dashboard/test-case/test-case-form/test-case-key-breadcrumb.resolver';
import { testPlanEditBreadcrumbResolver } from './dashboard/test-plans/test-plan-form/test-plan-edit-breadcrumb.resolver';
import { TestlaufNeuComponent } from './dashboard/testlauf-neu/testlauf-neu.component';
import { TestlaufDurchlaeufeComponent } from './dashboard/testlauf-durchlaeufe/testlauf-durchlaeufe.component';
import { TestlaufRunnerComponent } from './dashboard/testlauf-runner/testlauf-runner.component';
import { TestlaufErgebnisseComponent } from './dashboard/testlauf-ergebnisse/testlauf-ergebnisse.component';
import { ciMappingRedirectGuard } from './dashboard/ci-mapping/ci-mapping-redirect.guard';
import { canDeactivateTestlaufRunner } from './dashboard/testlauf-runner/can-deactivate-testlauf-runner.guard';
import { canDeactivateTestlaufNeu } from './dashboard/testlauf-neu/can-deactivate-testlauf-neu.guard';
import { testlaufLeserRedirectGuard } from './dashboard/testlauf-neu/testlauf-leser-redirect.guard';
import { TestStepComponent } from './dashboard/test-step/test-step.component';
import { TestStepFormComponent } from './dashboard/test-step/test-step-form/test-step-form.component';
import { canDeactivateTestStepForm } from './dashboard/test-step/test-step-form/can-deactivate-test-step-form.guard';
import { ModuleComponent } from './dashboard/module/module.component';
import { ModuleFormComponent } from './dashboard/module/module-form/module-form.component';
import { canDeactivateModuleForm } from './dashboard/module/module-form/can-deactivate-module-form.guard';
import { TestPlansComponent } from './dashboard/test-plans/test-plans.component';
import { TestPlanFormComponent } from './dashboard/test-plans/test-plan-form/test-plan-form.component';
import { canDeactivateTestPlanForm } from './dashboard/test-plans/test-plan-form/can-deactivate-test-plan-form.guard';
import { TestPlanReportComponent } from './dashboard/test-plans/test-plan-report/test-plan-report.component';
import { TestdatenblattComponent } from './dashboard/testdatenblatt/testdatenblatt.component';
import { TestdatenblattFormComponent } from './dashboard/testdatenblatt/testdatenblatt-form/testdatenblatt-form.component';
import { canDeactivateTestdatenblattForm } from './dashboard/testdatenblatt/testdatenblatt-form/can-deactivate-testdatenblatt-form.guard';
import { ObjectMappingListComponent } from './dashboard/object-mapping/object-mapping-list.component';
import { ObjectMappingDetailComponent } from './dashboard/object-mapping/object-mapping-detail.component';
import { RecordingLiveComponent } from './dashboard/object-mapping/recording-live/recording-live.component';
import { CaptureReviewComponent } from './dashboard/object-mapping/capture-review/capture-review.component';
import { DefectsPageComponent } from './dashboard/defects/defects-page.component';
import { DefectDetailComponent } from './dashboard/defects/defect-detail.component';
import { canDeactivateDefectDetail } from './dashboard/defects/can-deactivate-defect-detail.guard';
import { defectKeyBreadcrumbResolver } from './dashboard/defects/defect-key-breadcrumb.resolver';
import { objectMappingNameBreadcrumbResolver } from './dashboard/object-mapping/object-mapping-name-breadcrumb.resolver';
import { ClientManagementComponent } from './client-management/client-management.component';
import { CustomerDetailComponent } from './customer-detail/customer-detail.component';
import { UserEditPageComponent } from './customer-detail/user-edit-page/user-edit-page.component';
import { canDeactivateUserEditPage } from './customer-detail/user-edit-page/can-deactivate-user-edit-page.guard';
import { UserInvitePageComponent } from './customer-detail/user-invite-page/user-invite-page.component';
import { AgentCreatePageComponent } from './customer-detail/agent-create-page/agent-create-page.component';
import { AgentTeamUploadPageComponent } from './customer-detail/agent-team-upload-page/agent-team-upload-page.component';
import { AgentTokensPageComponent } from './customer-detail/agent-tokens-page/agent-tokens-page.component';
import { canDeactivateUserInvitePage } from './customer-detail/user-invite-page/can-deactivate-user-invite-page.guard';
import { CustomerFormComponent } from './client-management/customer-form/customer-form.component';
import { canDeactivateCustomerForm } from './client-management/customer-form/can-deactivate-customer-form.guard';
import { VerwaltungPageComponent } from './components/verwaltung/verwaltung-page.component';
import { verwaltungBreadcrumbSuffix } from './components/verwaltung/verwaltung-bereiche';
import { AccountComponent } from './account/account.component';
import { ProfilBearbeitenComponent } from './components/profil-bearbeiten/profil-bearbeiten.component';
import { canDeactivateProfilBearbeiten } from './components/profil-bearbeiten/can-deactivate-profil-bearbeiten.guard';
import { ProjectFormComponent } from './projects/project-form/project-form.component';
import { canDeactivateProjectForm } from './guards/can-deactivate-project-form.guard';
import { canDeactivateReleasePage } from './dashboard/release/can-deactivate-release-page.guard';
import { canDeactivateSettings } from './guards/can-deactivate-settings.guard';

const projectNameResolver: ResolveFn<string> = (route) => {
  const projectId = route.paramMap.get('projectId')!;
  return inject(ProjectService)
    .getById(projectId)
    .pipe(
      map((project) => project.name),
      catchError(() => of(projectId)),
    );
};

const projectRoleResolver: ResolveFn<boolean> = (route): Observable<boolean> =>
  inject(ProjectRoleService).ensureLoaded(route.paramMap.get('projectId'));

/** Fügt den Anforderungs-Key (`<key>`) als eigenes Breadcrumb-Segment vor der aktuellen Seite ein. */
const requirementKeyBreadcrumbResolver: ResolveFn<AppBreadcrumb[]> = (route) => {
  const projectId = route.paramMap.get('projectId')!;
  const requirementId = route.paramMap.get('id')!;
  const url = `/projects/${projectId}/requirements/${requirementId}/edit`;
  return inject(RequirementsService)
    .getById(projectId, requirementId)
    .pipe(
      map((requirement) => [{ label: requirement.extId || requirement.title || requirementId, url }]),
      catchError(() => of([{ label: requirementId, url }])),
    );
};

const loadRequirementForm = () =>
  import('./dashboard/requirements/requirement-form/requirement-form.component').then(
    (m) => m.RequirementFormComponent,
  );

const verwaltungBenutzerBreadcrumbPrefix: AppBreadcrumb[] = [
  { label: 'header.breadcrumb.home', url: '/' },
  { label: 'header.breadcrumb.verwaltung', url: '/settings' },
  { label: 'verwaltung.bereich.benutzer.titel', url: '/settings' },
];

export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'logout',
    canActivate: [logoutGuard],
    component: LoginComponent,
  },
  {
    path: 'invitations/accept',
    loadComponent: () =>
      import('./invitations/invitation-accept/invitation-accept.component').then((m) => m.InvitationAcceptComponent),
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        component: HomeComponent,
        data: { breadcrumb: 'header.breadcrumb.home' },
      },
      {
        path: 'dashboard',
        component: DashboardComponent,
        data: {
          breadcrumb: 'header.breadcrumb.dashboard',
          breadcrumbPrefix: [{ label: 'header.breadcrumb.home', url: '/' }],
        },
      },
      {
        path: 'client-management',
        canActivate: [platformAdminGuard],
        data: { breadcrumb: 'header.breadcrumb.clientManagement' },
        children: [
          {
            path: '',
            component: ClientManagementComponent,
            data: { breadcrumb: '' },
          },
          {
            path: 'new',
            component: CustomerFormComponent,
            canDeactivate: [canDeactivateCustomerForm],
            data: { breadcrumb: 'header.breadcrumb.customerFormCreate' },
          },
          {
            path: ':customerId/bearbeiten',
            component: CustomerFormComponent,
            canDeactivate: [canDeactivateCustomerForm],
            data: { breadcrumb: 'header.breadcrumb.customerFormEdit' },
          },
          {
            path: ':customerId',
            data: { breadcrumb: 'header.breadcrumb.customerDetail' },
            children: [
              {
                path: '',
                component: CustomerDetailComponent,
                data: { breadcrumb: '' },
              },
              {
                path: 'users/neu',
                component: UserInvitePageComponent,
                canDeactivate: [canDeactivateUserInvitePage],
                data: { breadcrumb: 'header.breadcrumb.userInvite' },
              },
              {
                path: 'agents/neu',
                component: AgentCreatePageComponent,
                data: { breadcrumb: 'header.breadcrumb.agentCreate' },
              },
              {
                path: 'agents/team',
                component: AgentTeamUploadPageComponent,
                data: { breadcrumb: 'header.breadcrumb.agentTeamUpload' },
              },
              {
                path: 'users/:userId/tokens',
                component: AgentTokensPageComponent,
                data: { breadcrumb: 'header.breadcrumb.agentTokens' },
              },
              {
                path: 'users/:userId/bearbeiten',
                component: UserEditPageComponent,
                canDeactivate: [canDeactivateUserEditPage],
                data: { breadcrumb: 'header.breadcrumb.userEdit' },
              },
            ],
          },
        ],
      },
      {
        path: 'settings/benutzer/neu',
        component: UserInvitePageComponent,
        canActivate: [tenantAdminGuard],
        canDeactivate: [canDeactivateUserInvitePage],
        data: {
          breadcrumb: 'header.breadcrumb.personEinladen',
          breadcrumbPrefix: verwaltungBenutzerBreadcrumbPrefix,
        },
      },
      {
        path: 'settings/benutzer/:userId/bearbeiten',
        component: UserEditPageComponent,
        canActivate: [tenantAdminGuard],
        canDeactivate: [canDeactivateUserEditPage],
        data: {
          breadcrumb: 'header.breadcrumb.userEdit',
          breadcrumbPrefix: verwaltungBenutzerBreadcrumbPrefix,
        },
      },
      {
        path: 'settings',
        canActivate: [tenantAdminGuard],
        data: {
          breadcrumb: 'header.breadcrumb.verwaltung',
          breadcrumbPrefix: [{ label: 'header.breadcrumb.home', url: '/' }],
        },
        children: [
          {
            // Der Bereich steht in der Query; die Rückfrage muss auch bei Zurück/Vor greifen, ohne dass
            // tenantAdminGuard (am Elternteil) bei jedem Bereichswechsel /users/me erneut abruft.
            path: '',
            component: VerwaltungPageComponent,
            canDeactivate: [canDeactivateSettings],
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            data: { breadcrumb: '', breadcrumbPrefix: null, breadcrumbSuffix: verwaltungBreadcrumbSuffix },
          },
        ],
      },
      {
        path: 'account',
        data: { breadcrumb: 'header.breadcrumb.account' },
        children: [
          {
            path: '',
            component: AccountComponent,
            data: { breadcrumb: '' },
          },
          {
            path: 'profile',
            component: ProfilBearbeitenComponent,
            canDeactivate: [canDeactivateProfilBearbeiten],
            data: { breadcrumb: 'header.breadcrumb.profileEdit' },
          },
        ],
      },
      {
        path: 'postfach',
        loadComponent: () => import('./postfach/postfach-page.component').then((m) => m.PostfachPageComponent),
        data: { breadcrumb: 'header.breadcrumb.postfach' },
      },
      {
        path: 'runners',
        loadComponent: () => import('./runners/runners-page.component').then((m) => m.RunnersPageComponent),
        data: { breadcrumb: 'header.breadcrumb.runners' },
      },
      {
        path: 'projects',
        data: { breadcrumb: 'header.breadcrumb.dashboard' },
        children: [
          { path: '', redirectTo: '/dashboard', pathMatch: 'full' },
          { path: 'new', redirectTo: '/settings?bereich=projekte&neu=1' },
          {
            path: ':projectId',
            providers: [ProjectRoleService],
            resolve: { breadcrumb: projectNameResolver, roleReady: projectRoleResolver },
            children: [
              { path: '', redirectTo: 'release', pathMatch: 'full' },
              {
                path: 'release',
                loadComponent: () =>
                  import('./dashboard/release/release-page.component').then((m) => m.ReleasePageComponent),
                canDeactivate: [canDeactivateReleasePage],
                data: { breadcrumb: 'header.breadcrumb.release' },
              },
              {
                path: 'documents',
                loadComponent: () =>
                  import('./dashboard/documents/dokumente-page.component').then((m) => m.DokumentePageComponent),
                data: { breadcrumb: 'header.breadcrumb.documents' },
              },
              {
                path: 'edit',
                component: ProjectFormComponent,
                canDeactivate: [canDeactivateProjectForm],
                data: { breadcrumb: 'header.breadcrumb.projectFormEdit' },
              },
              {
                path: 'test-results',
                data: { breadcrumb: 'header.breadcrumb.testResults' },
                children: [
                  {
                    path: '',
                    component: TestResultsComponent,
                    canDeactivate: [canDeactivateTestResults],
                    data: { breadcrumb: '' },
                  },
                  {
                    path: 'history/:testCaseId',
                    component: TestResultHistoryComponent,
                    data: { breadcrumb: 'header.breadcrumb.testResultHistory' },
                  },
                  {
                    path: ':runId',
                    component: TestResultDetailComponent,
                    data: { breadcrumb: 'header.breadcrumb.testResultDetail' },
                  },
                ],
              },
              {
                path: 'test-cases',
                data: { breadcrumb: 'header.breadcrumb.testCase' },
                children: [
                  { path: '', component: TestCaseComponent, data: { breadcrumb: '' } },
                  {
                    path: 'new',
                    component: TestCaseFormComponent,
                    canDeactivate: [canDeactivateTestCaseForm],
                    data: { breadcrumb: 'header.breadcrumb.testCaseFormCreate' },
                  },
                  {
                    path: ':id/edit',
                    component: TestCaseFormComponent,
                    canDeactivate: [canDeactivateTestCaseForm],
                    data: { breadcrumb: 'header.breadcrumb.testCaseFormEdit' },
                    resolve: { breadcrumb: testCaseKeyBreadcrumbResolver },
                  },
                ],
              },
              {
                path: 'test-runs',
                data: { breadcrumb: 'header.breadcrumb.testRun' },
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./dashboard/testlauf-uebersicht/testlauf-uebersicht.component').then(
                        (m) => m.TestlaufUebersichtComponent,
                      ),
                    data: { breadcrumb: '' },
                  },
                  // Vor ':runId' — sonst matcht die Detailseite und liest 'new' als Lauf-ID.
                  {
                    path: 'new',
                    component: TestlaufNeuComponent,
                    canActivate: [testlaufLeserRedirectGuard],
                    canDeactivate: [canDeactivateTestlaufNeu],
                    data: { breadcrumb: 'header.breadcrumb.testRunFormCreate' },
                  },
                  {
                    path: ':runId/edit',
                    component: TestlaufNeuComponent,
                    canActivate: [testlaufLeserRedirectGuard],
                    canDeactivate: [canDeactivateTestlaufNeu],
                    data: { breadcrumb: 'header.breadcrumb.testRunFormEdit' },
                  },
                  // Vor ':runId' — die Zwischenebene ist ein eigener Pfad unter dem Lauf.
                  {
                    path: ':runId/executions',
                    component: TestlaufDurchlaeufeComponent,
                    data: { breadcrumb: 'header.breadcrumb.testRunExecutions' },
                  },
                  // Die CI-Zuordnung lebt im Runner (Filter „Nicht zugeordnet“); der Pfad bleibt
                  // für alte Links bestehen.
                  {
                    path: ':runId/ci-mapping',
                    canActivate: [ciMappingRedirectGuard],
                    children: [],
                  },
                  {
                    path: ':runId/executions/:executionId/results',
                    component: TestlaufErgebnisseComponent,
                    data: { breadcrumb: 'header.breadcrumb.testRunResults' },
                  },
                  {
                    path: ':runId/executions/:executionId',
                    component: TestlaufRunnerComponent,
                    canDeactivate: [canDeactivateTestlaufRunner],
                    data: { breadcrumb: 'header.breadcrumb.testRunner' },
                  },
                  // Die Durchfuehrung haengt am Durchlauf, nicht am Lauf (NEX-1042). Der
                  // Lauf-Pfad bleibt als Einsprung fuer alte Links bestehen und fuehrt
                  // auf dessen Durchlaeufe.
                  {
                    path: ':runId',
                    redirectTo: ':runId/executions',
                    pathMatch: 'full',
                  },
                ],
              },
              {
                path: 'test-steps',
                data: { breadcrumb: 'header.breadcrumb.testStep' },
                children: [
                  { path: '', component: TestStepComponent, data: { breadcrumb: '' } },
                  {
                    path: 'new',
                    component: TestStepFormComponent,
                    canDeactivate: [canDeactivateTestStepForm],
                    data: { breadcrumb: 'header.breadcrumb.testStepFormCreate' },
                  },
                  {
                    path: ':id/edit',
                    component: TestStepFormComponent,
                    canDeactivate: [canDeactivateTestStepForm],
                    data: { breadcrumb: 'header.breadcrumb.testStepFormEdit' },
                  },
                ],
              },
              {
                path: 'test-modules',
                data: { breadcrumb: 'header.breadcrumb.module' },
                children: [
                  { path: '', component: ModuleComponent, data: { breadcrumb: '' } },
                  {
                    path: 'new',
                    component: ModuleFormComponent,
                    canDeactivate: [canDeactivateModuleForm],
                    data: { breadcrumb: 'header.breadcrumb.moduleNew' },
                  },
                  {
                    path: ':moduleId/edit',
                    component: ModuleFormComponent,
                    canDeactivate: [canDeactivateModuleForm],
                    data: { breadcrumb: 'header.breadcrumb.moduleEdit' },
                  },
                ],
              },
              {
                path: 'test-plans',
                data: { breadcrumb: 'header.breadcrumb.testPlan' },
                children: [
                  { path: '', component: TestPlansComponent, data: { breadcrumb: '' } },
                  {
                    path: 'new',
                    component: TestPlanFormComponent,
                    canDeactivate: [canDeactivateTestPlanForm],
                    data: { breadcrumb: 'header.breadcrumb.testPlanNew' },
                  },
                  {
                    path: ':planId/edit',
                    component: TestPlanFormComponent,
                    canDeactivate: [canDeactivateTestPlanForm],
                    data: { breadcrumb: 'header.breadcrumb.testPlanEdit' },
                    resolve: { breadcrumb: testPlanEditBreadcrumbResolver },
                  },
                  {
                    path: ':planId/report',
                    component: TestPlanReportComponent,
                    data: { breadcrumb: 'header.breadcrumb.testPlanReport' },
                  },
                ],
              },
              {
                path: 'testdatenblaetter',
                data: { breadcrumb: 'header.breadcrumb.testdatenblatt' },
                children: [
                  { path: '', component: TestdatenblattComponent, data: { breadcrumb: '' } },
                  {
                    path: 'neu',
                    component: TestdatenblattFormComponent,
                    canDeactivate: [canDeactivateTestdatenblattForm],
                    data: { breadcrumb: 'header.breadcrumb.testdatenblattFormCreate' },
                  },
                  {
                    path: ':tdbId/bearbeiten',
                    component: TestdatenblattFormComponent,
                    canDeactivate: [canDeactivateTestdatenblattForm],
                    data: { breadcrumb: 'header.breadcrumb.testdatenblattFormEdit' },
                  },
                ],
              },
              {
                path: 'object-mappings',
                data: { breadcrumb: 'header.breadcrumb.objectMappings' },
                children: [
                  { path: '', component: ObjectMappingListComponent, data: { breadcrumb: '' } },
                  {
                    path: ':mappingId',
                    component: ObjectMappingDetailComponent,
                    data: { breadcrumb: 'header.breadcrumb.objectMappingDetail' },
                    resolve: { breadcrumb: objectMappingNameBreadcrumbResolver },
                  },
                  {
                    path: ':mappingId/recording/:jobId',
                    component: RecordingLiveComponent,
                    data: { breadcrumb: 'header.breadcrumb.objectMappingRecording' },
                  },
                  {
                    path: ':mappingId/review',
                    component: CaptureReviewComponent,
                    data: { breadcrumb: 'header.breadcrumb.objectMappingReview' },
                  },
                ],
              },
              {
                path: 'defects',
                data: { breadcrumb: 'defect.list.breadcrumb' },
                children: [
                  { path: '', component: DefectsPageComponent, data: { breadcrumb: '' } },
                  {
                    path: ':defectId',
                    component: DefectDetailComponent,
                    canDeactivate: [canDeactivateDefectDetail],
                    data: { breadcrumb: 'defect.detail.breadcrumb' },
                    resolve: { breadcrumb: defectKeyBreadcrumbResolver },
                  },
                ],
              },
              {
                path: 'requirements',
                data: { breadcrumb: 'header.breadcrumb.requirements' },
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./dashboard/requirements/requirements.component').then((m) => m.RequirementsComponent),
                    data: { breadcrumb: '' },
                  },
                  {
                    path: 'new',
                    loadComponent: loadRequirementForm,
                    canDeactivate: [canDeactivateRequirementForm],
                    data: { breadcrumb: 'header.breadcrumb.requirementFormCreate' },
                  },
                  {
                    path: ':id/edit',
                    loadComponent: loadRequirementForm,
                    canDeactivate: [canDeactivateRequirementForm],
                    data: { breadcrumb: 'header.breadcrumb.requirementFormEdit' },
                  },
                  {
                    path: ':id/historie',
                    component: RequirementHistoryComponent,
                    resolve: { breadcrumbPrefix: requirementKeyBreadcrumbResolver },
                    data: { breadcrumb: 'header.breadcrumb.requirementHistory' },
                  },
                ],
              },
              // Legacy-Redirects: alte Singular-URLs auf die neuen Plural-Pfade.
              // pathMatch 'prefix' haengt Restsegmente an (z. B. /new, /:id/edit).
              { path: 'test-case', redirectTo: 'test-cases', pathMatch: 'prefix' },
              { path: 'test-step', redirectTo: 'test-steps', pathMatch: 'prefix' },
              { path: 'module', redirectTo: 'test-modules', pathMatch: 'prefix' },
            ],
          },
        ],
      },
    ],
  },
  { path: '**', redirectTo: 'login' },
];
